import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { lazyWithPreload } from "utils/lazyWithPreload";

import { usePreloadComponents } from "hooks/usePreloadedComponents";
import { alterDatabase } from "services/databases/alterDatabase";
import { CreateEditDatabaseInput } from "services/databases/types";
import { Database } from "services/databases/useDatabasesNames";
import { ReactQueryKeysAccount } from "services/queryKeys";

import { StatusMessageType } from "components/StatusMessageQueue/StatusMessageQueueProvider";
import useStatusMessageQueue from "components/StatusMessageQueue/hooks/useStatusMessageQueue";

const DatabaseModal = lazyWithPreload(
  () => import("../DatabaseModal/DatabaseModal")
);
export const useEditDatabase = () => {
  const [editDatabase, setEditDatabase] = useState<Database | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const { t } = useTranslation();

  usePreloadComponents([DatabaseModal]);

  const { putStatusMessage } = useStatusMessageQueue();

  const openEditDatabase = (database: Database) => {
    setEditDatabase(database);
  };

  const closeEditDatabase = () => {
    if (isLoading) {
      return;
    }
    setEditDatabase(null);
  };

  const confirmEdit = async (database: CreateEditDatabaseInput) => {
    try {
      if (!editDatabase) return;
      setIsLoading(true);
      await alterDatabase({
        database,
        initialDatabase: editDatabase,
      });
      await queryClient.cancelQueries({
        queryKey: [ReactQueryKeysAccount.databasesNames],
      });
      await queryClient.invalidateQueries({
        queryKey: [ReactQueryKeysAccount.databasesNames],
      });

      putStatusMessage({
        message: t("workspace.update_database.success", {
          databaseName: database.catalogName,
        }),
        type: StatusMessageType.Success,
      });
      setIsLoading(false);
      setEditDatabase(null);
    } catch (error: any) {
      console.log(error);
      setIsLoading(false);
      putStatusMessage({
        message: error.message,
        type: StatusMessageType.Error,
      });
      await queryClient.invalidateQueries({
        queryKey: [ReactQueryKeysAccount.databasesNames],
      });
    }
  };

  const editDatabaseMarkup = editDatabase ? (
    <DatabaseModal
      title={t("workspace.update_database.title")}
      primaryButtonText="workspace.update_database.submit"
      onClose={closeEditDatabase}
      isLoading={isLoading}
      onSubmit={confirmEdit}
      database={editDatabase}
    />
  ) : null;

  return {
    editDatabaseMarkup,
    openEditDatabase,
  };
};
